import { Form, Formik, useFormikContext } from "formik";
import React, { useEffect, useRef, useState } from "react";
import AddButton from "../../buttons/button";
import moment from "moment";
import errorToast from "../../helper-components/error-toast";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import CloseIcon from "../../../assets/icons/close-icon";
import InputField from "../../forms/input-field";
import SelectField from "../../forms/select-field";
import { ChevronDownIcon, PlusIcon } from "lucide-react";
import { Package } from "lucide-react";

import { minDate } from "../../helper-components/helper-function";
import LoadingOverlay from "../../helper-components/overlay-loading";
import { stockTransferBatchAction } from "../../../redux/actions/inventory-action";

const StockTransferBatchesPanel = ({
  setOpen,
  itemId,
  setItemId,
  binLocationList = [],
  disName = "",
  disabled = false,
  itemValues = {},
  open,
  prefix = "gort",
}) => {
  const batchRef = useRef();
  const [loading, setLoading] = useState(false);
  const [openBatchIndex, setOpenBatchIndex] = useState(null);
  const defaultSelectedItem = itemValues?.data?.find(
    (item, index) => itemId === item?.item_id
  );
  const [newBatch, setNewBatch] = useState(
    defaultSelectedItem?.item_batches?.length > 0 ? false : true
  );

  useEffect(() => {
    if (open) {
      setNewBatch(defaultSelectedItem?.item_batches?.length > 0 ? false : true);
      batchRef.current?.focus();
    }
  }, [open]);

  const { setFieldValue } = useFormikContext();
  const batches = defaultSelectedItem?.item_batches;
  const dispatch = useDispatch();
  const defaultSelectedItemIndex = itemValues?.data?.findIndex(
    (item, index) => itemId === item?.item_id
  );

  const validationSchema = Yup.object().shape({
    item_batch_purchase_rate: Yup.number()
      .typeError("Must be a number")
      .required("Purchase rate is required")
      .moreThan(0, "Purchase rate must be greater than 0"),
    item_batch_unit_price: Yup.number()
      .typeError("Must be a number")
      .required("Unit price is required")
      .moreThan(0, "Unit price must be greater than 0"),

    item_batch_quantity: Yup.number()
      .integer("Quantity must be an integer")
      .required("Sellable quantity is required")
      .typeError("Must be a number")
      .moreThan(0, "Qty must be greater than 0"),
    item_batch_free_quantity: Yup.number()
      .transform((value) =>
        typeof value === "string" ? parseInt(value, 10) : value
      ) // Transform string to number
      .typeError("Must be a number")
      .integer("Quantity must be an integer"),
    item_batch_number: Yup.string().required("Batch number is required"),
    // to_bin_id: Yup.string().required("Bin ID is required"),
    item_exp_date: Yup.date().required("Expiration date is required"),
  });
  const today = new Date();
  const today2 = moment().format("YYYY-MM-DD");
  const sixMonthsLater = new Date(today.setMonth(today.getMonth() + 6));
  const initialValues =
    {
      item_id: defaultSelectedItem?.item_id,
      item_batch_number: "",
      remainingQty: 0,
      item_batch_quantity: 0,
      from_bin_location: "",
      to_bin_location: "receiving-bin",
      to_bin_id:
        itemValues?.stock_management_action === "stock_in" ||
          itemValues?.stock_management_action === "in_store_transfer"
          ? binLocationList?.find((bin) => bin?.actualLabel === "receiving-bin")
            ?.value
          : "",
      from_bin_id: "",
      created_date: "",
      item_exp_date: moment(sixMonthsLater).format("YYYY-MM-DD"),
      item_batch_free_quantity: 0,
      item_batch_purchase_rate: 0,
      item_batch_unit_price: 0,
    } ?? {};

  const getAvailableBatches = (values, defaultSelectedItemIndex) => {
    return defaultSelectedItem?.itemBatchList
      ?.filter((item) => {
        if (!item?.item_batch_number) return false;
        const isAlreadyUsed = defaultSelectedItem?.item_batches?.some(
          (batch, index) =>
            index !== openBatchIndex &&
            batch?.item_batch_number === item?.item_batch_number &&
            batch?.created_date === item?.created_date
        );
        if (isAlreadyUsed) return false;
        const sumBatchQuantity =
          values?.item_batches
            ?.filter(
              (batch) =>
                batch?.created_date === item?.created_date &&
                batch?.item_batch_number === item?.item_batch_number
            )
            .reduce(
              (sum, batch) =>
                sum + (parseFloat(batch?.item_batch_quantity) || 0),
              0
            ) * parseFloat(defaultSelectedItem?.base_quantity);
        console.log(sumBatchQuantity, "defaultSelectedItem");

        const remainingQty =
          parseFloat(item?.item_quantity) - (sumBatchQuantity || 0);
        return remainingQty > 0;
      })
      .map((item) => {
        const sumBatchQuantity =
          values?.item_batches
            ?.filter(
              (batch) =>
                batch?.created_date === item?.created_date &&
                batch?.item_batch_number === item?.item_batch_number
            )
            .reduce(
              (sum, batch) =>
                sum + (parseFloat(batch?.item_batch_quantity) || 0),
              0
            ) || 0;

        const remainingQty =
          parseFloat(item?.item_quantity) -
          sumBatchQuantity * parseFloat(defaultSelectedItem?.base_quantity);

        return {
          label: (
            <span className="flex flex-col">
              <span>Avail Qty: {remainingQty}</span>
              <span>Btch Num: {item?.item_batch_number}</span>
            </span>
          ),
          remainingQty: remainingQty,
          value: item?.item_batch_number,
          from_bin_id: item?.to_bin_id,
          from_bin_location: item?.item_rack_location,
          created_date: item?.created_date,
          ...item,
        };
      });
  };

  const handleShowErrorToast = async (item_id = "", errors, values) => {
    // If values exist, check for batch field errors directly (not as an array)
    if (values) {
      const batchError = errors;

      let hasError = false;

      if (batchError?.item_batch_purchase_rate && !hasError) {
        errorToast(batchError.item_batch_purchase_rate);
        hasError = true;
      } else if (batchError?.item_batch_unit_price && !hasError) {
        errorToast(batchError.item_batch_unit_price);
        hasError = true;
      } else if (batchError?.item_sellable_quantity && !hasError) {
        errorToast(batchError.item_sellable_quantity);
        hasError = true;
      } else if (batchError?.item_batch_free_quantity && !hasError) {
        errorToast(batchError.item_batch_free_quantity);
        hasError = true;
      } else if (batchError?.item_batch_number && !hasError) {
        errorToast(batchError.item_batch_number);
        hasError = true;
      } else if (batchError?.item_exp_date && !hasError) {
        errorToast(batchError.item_exp_date);
        hasError = true;
      }
    } else {
    }
  };

  const handleDeleteBatch = (batchIndex) => {
    const updatedBatches = [...(batches || [])];
    updatedBatches.splice(batchIndex, 1);

    const sumQuantity = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_quantity) || 0),
      0
    );
    const totalDiscount = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_discount_amount) || 0),
      0
    );
    const totalTax = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_tax_amount) || 0),
      0
    );
    const totalAmt = updatedBatches.reduce(
      (sum, batch) =>
        sum + (parseFloat(batch.item_batch_total_purchase_rate) || 0),
      0
    );
    const totalFreeQty = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_free_quantity) || 0),
      0
    );

    const updatedDataRow = {
      ...defaultSelectedItem,
      item_batches: updatedBatches,
      quantity: sumQuantity,
      item_quantity: sumQuantity,
      item_free_quantity: +totalFreeQty,
      gort_total_discount: +totalDiscount,
      item_discount_amount: +totalDiscount,
      item_tax_amount: +totalTax?.toFixed(2),
      // item_discount_percentage: +item_discount_percentage,
      item_gst: updatedBatches?.[0]?.item_total_tax_percentage || 0,
      item_sgst: +(totalTax / 2)?.toFixed(2),
      item_cgst: +(totalTax / 2)?.toFixed(2),
      item_igst: 0,
      item_pack_size: defaultSelectedItem?.base_quantity * sumQuantity,
      item_total_amount: totalAmt.toFixed(2),
    };
    setFieldValue(`data.${defaultSelectedItemIndex}`, updatedDataRow);
    const updatedData = [...itemValues.data];
    updatedData[defaultSelectedItemIndex] = updatedDataRow;

    // Inline total, tax, discount calculations
    const raw_total_amount = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_total_amount) || 0);
    }, 0);

    const total_tax = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_tax_amount) || 0);
    }, 0);

    const total_discount = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_discount_amount) || 0);
    }, 0);

    // Subtotal = Total - Tax - Discount
    const sub_total = (raw_total_amount - total_tax - total_discount).toFixed(
      2
    );

    const total_amount = Math.round(raw_total_amount).toFixed(2);
    const round_off_value = (
      Math.round(raw_total_amount) - raw_total_amount
    ).toFixed(2);

    // Set all values to form
    setFieldValue(`${prefix}_sub_total`, sub_total);
    setFieldValue(`${prefix}_total_gst`, total_tax);
    setFieldValue(`${prefix}_total_discount`, total_discount);
    setFieldValue(`${prefix}_total_amount`, total_amount);
    setFieldValue(`${prefix}_round_off_value`, round_off_value);
    setFieldValue(`${prefix}_tcs_or_tds_amount`, 0);
    setFieldValue(`${prefix}_tcs_or_tds_percentage_id`, "");
    setFieldValue(`${prefix}_tcs_or_tds_percentage`, 0);
    setFieldValue(`${prefix}_tds_tcs_type`, "");

    setNewBatch(false);
    setOpenBatchIndex(null);
  };

  const innerOnSubmit = (values) => {
    // Make a copy of current batches array or empty array if undefined
    const item_batches = [...(batches || [])];

    if (
      typeof openBatchIndex === "number" &&
      openBatchIndex >= 0 &&
      openBatchIndex < item_batches.length
    ) {
      // Update existing batch at openBatchIndex
      item_batches[openBatchIndex] = values;
    } else {
      // Add new batch
      item_batches.push(values);
    }

    // Now compute all totals based on updated item_batches
    const sumQuantity = item_batches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_quantity) || 0),
      0
    );
    const totalDiscount = item_batches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_discount_amount) || 0),
      0
    );
    const totalTax = item_batches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_tax_amount) || 0),
      0
    );
    const totalAmt = item_batches.reduce(
      (sum, batch) =>
        sum + (parseFloat(batch.item_batch_total_purchase_rate) || 0),
      0
    );
    const totalFreeQty = item_batches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_free_quantity) || 0),
      0
    );

    const updatedDataRow = {
      ...defaultSelectedItem,
      item_batches,
      quantity: sumQuantity,
      item_quantity: sumQuantity,
      item_free_quantity: +totalFreeQty,
      gort_total_discount: +totalDiscount,
      item_discount_amount: +totalDiscount,
      item_tax_amount: +totalTax?.toFixed(2),
      item_gst: item_batches?.[0]?.item_total_tax_percentage || 0,
      item_sgst: +(totalTax / 2)?.toFixed(2),
      item_cgst: +(totalTax / 2)?.toFixed(2),
      item_igst: 0,
      item_pack_size: defaultSelectedItem?.base_quantity * sumQuantity,
      item_total_amount: totalAmt.toFixed(2),
    };

    // Update the main data array
    setFieldValue(`data.${defaultSelectedItemIndex}`, updatedDataRow);
    const updatedData = [...itemValues.data];
    updatedData[defaultSelectedItemIndex] = updatedDataRow;

    // Inline total, tax, discount calculations
    const raw_total_amount = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_total_amount) || 0);
    }, 0);

    const total_tax = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_tax_amount) || 0);
    }, 0);

    const total_discount = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_discount_amount) || 0);
    }, 0);

    // Subtotal = Total - Tax - Discount
    const sub_total = (raw_total_amount - total_tax - total_discount).toFixed(
      2
    );
    console.log(
      sub_total,
      raw_total_amount,
      total_tax,
      total_discount,
      "gfgsdgsdgsgsdgsdgsdgsdg"
    );
    const total_amount = Math.round(raw_total_amount).toFixed(2);
    const round_off_value = (
      Math.round(raw_total_amount) - raw_total_amount
    ).toFixed(2);

    // Clear TCS/TDS

    // Set all values to form
    setFieldValue(`${prefix}_sub_total`, sub_total);
    setFieldValue(`${prefix}_total_gst`, total_tax);
    setFieldValue(`${prefix}_total_discount`, total_discount);
    setFieldValue(`${prefix}_total_amount`, total_amount);
    setFieldValue(`${prefix}_round_off_value`, round_off_value);
    setFieldValue(`${prefix}_tcs_or_tds_amount`, 0);
    setFieldValue(`${prefix}_tcs_or_tds_percentage_id`, "");
    setFieldValue(`${prefix}_tcs_or_tds_percentage`, 0);
    setFieldValue(`${prefix}_tds_tcs_type`, "");

    setNewBatch(false);
    setOpenBatchIndex(null);
  };

  useEffect(() => {
    if (itemId && open) {
      dispatch(stockTransferBatchAction(itemId, onSuccess, onError));
    }
  }, [itemId, open]);
  const onSuccess = (data) => {
    setFieldValue(`data.${defaultSelectedItemIndex}.itemBatchList`, data?.data);
  };
  const onError = () => { };

  const defaultInputClass =
    "min-h-[42px] rounded-[8px] px-2 bg-[var(--vert-input-bg)] text-[var(--vert-input-color)] text-[length:var(--vert-input-font-size)] font-[var(--vert-input-font-weight)] rounded input-field-no-spin-buttons placeholder:text-[var(--vert-input-place-color)] placeholder:text-[length:var(--vert-input-place-font-size)] placeholder:font-[var(--vert-input-place-font-weight)] focus:outline-none focus:border-[var(--vert-input-focus)] focus:text-[var(--vert-input-color)] focus:bg-[var(--vert-input-bg)]";
  const labelClass =
    "whitespace-nowrap flex text-[var(--vert-input-label-color)] text-[length:var(--vert-input-label-font-size)] font-[var(--vert-input-label-font-weight)]";
  const errorBorder = "border border-[var(--vert-input-error-border)]";
  const inputBorder = "border border-[var(--vert-input-border)]";
  const toggleBatch = (index) => {
    setOpenBatchIndex((prev) => (prev === index ? null : index));
  };

  const expiryRef = useRef();
  const fromBinRef = useRef();
  const toBinRef = useRef();
  const qtyRef = useRef();
  const freeQtyRef = useRef();
  const mrpRef = useRef();
  const basePrRef = useRef();

  console.log(
    getAvailableBatches(itemValues, defaultSelectedItemIndex),
    "defaultSelectedItem"
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          setOpen(false);
        }}
      >
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[600px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl">
                {/* Header */}

                <Formik
                  enableReinitialize={true}
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={innerOnSubmit}
                >
                  {({
                    values,
                    handleSubmit,
                    errors,
                    setFieldValue,
                    setValues,
                  }) => {
                    return (
                      <Form className="h-full relative">
                        <div
                          className="h-[61px] bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b"
                          style={{ borderColor: "var(--slide-panel-border)" }}
                        >
                          <div
                            style={{
                              maxHeight: "32px",
                              color: "var(--slide-panel-h-fg)",
                              fontSize: "var(--slide-panel-h-fs)",
                              fontWeight: "var(--slide-panel-h-fw)",
                            }}
                            className="min-w-[75%] "
                          >
                            {newBatch && openBatchIndex
                              ? "Update Batch"
                              : newBatch
                                ? "Add New Batch"
                                : ``}
                          </div>
                          <AddButton
                            onclkFn={() => {
                              setOpen(false);
                            }}
                            icon={
                              <CloseIcon
                                width="12px"
                                height="12px"
                                fill="var(--close-button-fg)"
                              />
                            }
                            text={
                              <span>
                                Close <span className="opacity-50">(X)</span>{" "}
                              </span>
                            }
                            className="flex h-[32px]  items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                          />
                        </div>
                        {newBatch ? (
                          <div
                            style={{
                              height: `calc(100% - 9rem)`,
                              minHeight: `calc(100% - 9rem)`,
                            }}
                            className="px-4 pt-5 relative"
                          >
                            <div className="h-full w-full flex flex-col gap-2  overflow-y-auto justify-between">
                              <div className="flex flex-col gap-4">
                                {itemValues?.stock_management_action ===
                                  "stock_in" ? (
                                  <InputField
                                    maxLength={10}
                                    autoFocus={true}
                                    inputRef={batchRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    isRequired={true}
                                    containerClass="w-[100%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    type="text"
                                    name="item_batch_number"
                                    placeholder="Batch No."
                                    label="Batch No."
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    onKeyDown={(e) => {
                                      const regex = /[a-zA-Z0-9]/;
                                      if (e.key === " " || !regex.test(e.key)) {
                                        e.preventDefault();
                                      }
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        e.preventDefault();
                                        mrpRef.current?.focus();
                                      }
                                    }}
                                  />
                                ) : (
                                  <SelectField
                                    maxLength={10}
                                    general_color={false}
                                    // autoFocus={true}
                                    isDisabled={disabled}
                                    handleSelected={(sel) => {
                                      console.log(sel, "dffsdfsfdsfdsfsdfsdff");
                                      if (
                                        itemValues?.stock_management_action ===
                                        "stock_out" ||
                                        itemValues?.stock_management_action ===
                                        "in_store_transfer"
                                      ) {
                                        setFieldValue(
                                          `item_batch_free_quantity`,
                                          sel?.item_batch_free_quantity ?? 0
                                        );
                                        setFieldValue(
                                          `item_batch_purchase_rate`,
                                          sel?.item_batch_purchase_rate ?? 0
                                        );
                                        setFieldValue(
                                          `item_batch_unit_price`,
                                          sel?.item_batch_unit_price ?? 0
                                        );
                                        setFieldValue(
                                          `from_bin_id`,
                                          sel?.from_bin_id ?? ""
                                        );

                                        setFieldValue(
                                          `from_bin_location`,
                                          sel?.from_bin_location ?? ""
                                        );

                                        setFieldValue(
                                          `created_date`,
                                          sel?.created_date ?? ""
                                        );
                                        setFieldValue(
                                          `remainingQty`,
                                          sel?.remainingQty ?? 0
                                        );
                                      }
                                    }}
                                    // borderclass=""
                                    // defaultSelectClass="border-0"
                                    selectClass="w-full"
                                    defaultValue={
                                      values?.item_batch_number
                                        ? {
                                          label: values?.item_batch_number,
                                          value: "",
                                        }
                                        : null
                                    }
                                    errMsgContainerClass="hidden"
                                    id={`item_batch_number`}
                                    isContainer={false}
                                    options={getAvailableBatches(
                                      itemValues,
                                      defaultSelectedItemIndex
                                    )}
                                    name={`item_batch_number`}
                                    placeholder={"select batch"}
                                  />
                                )}

                                {(itemValues?.stock_management_action ===
                                  "stock_out" ||
                                  itemValues?.stock_management_action ===
                                  "in_store_transfer") &&
                                  !disabled && (
                                    <div className="flex rounded-[8px] justify-between items-center px-3  min-h-[42px] border border-[var(--vert-input-border)]">
                                      <span className="text-[var(--slide-panel-label)] text-[length:var(--slide-panel-label-fs)] font-[var(--slide-panel-label-fw)]">
                                        Available Quantity in{" "}
                                        {defaultSelectedItem?.item_uom}
                                      </span>
                                      <span className="text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)]">
                                        {values?.item_batch_number
                                          ? (
                                            parseFloat(values?.remainingQty) /
                                            parseFloat(
                                              defaultSelectedItem?.base_quantity ||
                                              1
                                            )
                                          ).toFixed(2)
                                          : "0.00"}
                                      </span>
                                    </div>

                                    // <input
                                    //   value={(values?.item_batch_number
                                    //     ? parseFloat(values?.remainingQty) /
                                    //       parseFloat(
                                    //         defaultSelectedItem?.base_quantity
                                    //       )
                                    //     : 0
                                    //   ).toFixed(2)}
                                    // />
                                    // <td className="border-D0D5DD border border-1 py-2 px-1 text-secondary fw_400 text-sm">
                                    //   {(values?.item_batch_number
                                    //     ? parseFloat(values?.remainingQty) /
                                    //       parseFloat(
                                    //         defaultSelectedItem?.base_quantity
                                    //       )
                                    //     : 0
                                    //   ).toFixed(2)}
                                    // </td>
                                  )}

                                <div className="flex gap-5">
                                  <InputField
                                    inputRef={mrpRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_batch_unit_price"
                                    placeholder="MRP"
                                    label="MRP (Per 1 Qty)"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        e.preventDefault();
                                        basePrRef.current?.focus();
                                      }
                                    }}
                                    onFocus={(e) => e.target.select()}
                                  />
                                  <InputField
                                    inputRef={basePrRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_batch_purchase_rate"
                                    placeholder="Final PR"
                                    label="Final PR (Per 1 Qty)"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        e.preventDefault();
                                        qtyRef.current?.focus();
                                      }
                                    }}
                                    onFocus={(e) => e.target.select()}
                                  />
                                </div>
                                <div className="flex gap-5">
                                  <InputField
                                    maxLength={10}
                                    inputRef={qtyRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_batch_quantity"
                                    placeholder="Quantity"
                                    label="Quantity"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    handleChange={(e) => {
                                      const raw = e.target.value;
                                      if (raw === "") {
                                        setFieldValue(`item_batch_quantity`, "");
                                        return;
                                      }
                                      let value = parseInt(raw, 10);
                                      if (Number.isNaN(value)) return;
                                      const createdDate = values.created_date;
                                      const itemBatch =
                                        defaultSelectedItem?.itemBatchList?.find(
                                          (bch) =>
                                            bch.item_batch_number ===
                                            values.item_batch_number
                                        );
                                      const totalItemQuantity =
                                        (parseFloat(itemBatch?.item_quantity) /
                                          parseFloat(
                                            defaultSelectedItem?.base_quantity || 1
                                          )) || 0;
                                      const remainingQty = parseInt(
                                        totalItemQuantity,
                                        10
                                      );
                                      // Only cap when we have a valid batch and positive remaining qty
                                      if (
                                        itemBatch &&
                                        !Number.isNaN(remainingQty) &&
                                        remainingQty >= 0 &&
                                        value > remainingQty
                                      ) {
                                        value =
                                          itemValues?.stock_management_action ===
                                            "stock_in"
                                            ? value
                                            : remainingQty;
                                      }
                                      setFieldValue(
                                        `item_batch_quantity`,
                                        value
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const raw = e.target.value;
                                      if (raw === "") {
                                        setFieldValue(`item_batch_quantity`, "");
                                        return;
                                      }
                                      let value = parseInt(raw, 10);
                                      if (Number.isNaN(value)) return;
                                      const createdDate = values.created_date;
                                      const itemBatch =
                                        defaultSelectedItem?.itemBatchList?.find(
                                          (bch) =>
                                            bch.item_batch_number ===
                                            values.item_batch_number
                                        );
                                      const totalItemQuantity =
                                        (parseFloat(itemBatch?.item_quantity) /
                                          parseFloat(
                                            defaultSelectedItem?.base_quantity || 1
                                          )) || 0;
                                      const remainingQty = parseInt(
                                        totalItemQuantity,
                                        10
                                      );
                                      // Only cap when we have a valid batch and valid remaining qty
                                      if (
                                        itemBatch &&
                                        !Number.isNaN(remainingQty) &&
                                        remainingQty >= 0 &&
                                        value > remainingQty
                                      ) {
                                        if (
                                          itemValues?.stock_management_action ===
                                          "stock_out"
                                        ) {
                                          errorToast(
                                            "Quantity exceeds available stock"
                                          );
                                        }
                                        value =
                                          itemValues?.stock_management_action ===
                                            "stock_in"
                                            ? value
                                            : remainingQty;
                                      }
                                      setFieldValue(
                                        `item_batch_quantity`,
                                        value
                                      );
                                    }}
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        e.preventDefault();
                                        freeQtyRef.current?.focus();
                                      }
                                    }}
                                    onFocus={(e) => e.target.select()}
                                  />
                                  <InputField
                                    inputRef={freeQtyRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={false}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_batch_free_quantity"
                                    placeholder="Free Qty"
                                    label="Free Qty"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        e.preventDefault();
                                        fromBinRef.current?.focus();
                                        toBinRef.current?.focus();
                                      }
                                    }}
                                    onFocus={(e) => e.target.select()}
                                  />
                                </div>
                                <div className="flex gap-5">
                                  <SelectField
                                    maxLength={10}
                                    ref={fromBinRef}
                                    placeholder="Select Bin"
                                    parentClass="flex flex-col gap-2"
                                    containerClass="w-[50%]"
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    general_color={false}
                                    autoFocus={values?.item_batch_number}
                                    defaultValue={
                                      binLocationList?.find(
                                        (bin) =>
                                          bin?.value === values?.from_bin_id
                                      )
                                        ? {
                                          label: binLocationList?.find(
                                            (bin) =>
                                              bin?.value === values?.from_bin_id
                                          )?.label,
                                          value: "",
                                        }
                                        : null
                                    }
                                    handleSelected={(selectedoption) => {
                                      setFieldValue(
                                        `from_bin_location`,
                                        selectedoption?.actualLabel
                                      );
                                    }}
                                    isDisabled={
                                      disabled ||
                                      itemValues?.stock_management_action ===
                                      "stock_in"
                                    }
                                    options={
                                      itemValues?.stock_management_action ===
                                        "stock_in"
                                        ? []
                                        : binLocationList
                                    }
                                    label={"From Bin"}
                                    id={`from_bin_id`}
                                    name={`from_bin_id`}
                                    isSearchable={true}
                                    errMsgContainerClass="ps-2 hidden"
                                    labelWidth="7rem"
                                  />
                                  <SelectField
                                    maxLength={10}
                                    // autoFocus={true}
                                    // ref={(el) => {
                                    //   if (binRef) binRef.current = el;
                                    // }}
                                    ref={toBinRef}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    isDisabled={
                                      disabled ||
                                      itemValues?.stock_management_action ===
                                      "stock_out"
                                    }
                                    placeholder="Select Bin"
                                    options={
                                      itemValues?.stock_management_action ===
                                        "stock_out"
                                        ? []
                                        : binLocationList
                                    }
                                    label="To Bin"
                                    id={`to_bin_id`}
                                    name={`to_bin_id`}
                                    isSearchable={true}
                                    isRequired={false}
                                    defaultValue={
                                      binLocationList?.find(
                                        (bin) => bin?.value === values?.to_bin_id
                                      )
                                        ? {
                                          label: binLocationList?.find(
                                            (bin) =>
                                              bin?.value === values?.to_bin_id
                                          )?.label,
                                          value: "",
                                        }
                                        : null
                                    }
                                    handleSelected={(selectedoption) => {
                                      setFieldValue(
                                        `to_bin_location`,
                                        selectedoption?.actualLabel
                                      );
                                    }}
                                    errMsgContainerClass="hidden"
                                  />
                                </div>
                                <div className="flex gap-5">
                                  <InputField
                                    inputRef={expiryRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="date"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_exp_date"
                                    placeholder="Expiry Date"
                                    label="Expiry Date"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    min={moment(values.item_exp_date).format(
                                      "YYYY-MM-DD"
                                    )}
                                  />
                                  <div className="w-[50%]"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            style={{
                              height: `calc(100% - 61px)`,
                              minHeight: `calc(100% - 61px)`,
                            }}
                            className="px-4 pt-5 relative"
                          >
                            <div className="h-[2rem] mb-[1rem] flex justify-between items-center">
                              <span className="text-[var(--slide-panel-h-fg)] text-[length:var(--slide-panel-h-fs)]  font-[var(--slide-panel-h-fw)]">
                                Batches For {defaultSelectedItem?.item_name}
                              </span>
                              {!disabled && (
                                <AddButton
                                  type="button"
                                  icon={
                                    <PlusIcon
                                      size={18}
                                      fill="var(--primary-button-fg-color)"
                                    />
                                  }
                                  className="border-radius hover:bg-[var(--primary-button-hover-color)] flex justify-center gap-[8px] h-[36px] px-[12px]  items-center   whitespace-nowrap   transition duration-300 ease-in-out   text-[length:--primary-button-font-size] font-[var(--primary-button-font-weight)] bg-[var(--primary-button-bg-color)]  cursor-pointer text-[var(--primary-button-fg-color)]"
                                  onclkFn={() => {
                                    setNewBatch(true);
                                    const preservedBatches =
                                      values.item_batches;
                                    const { item_batches: _, ...restInitials } =
                                      initialValues;
                                    setValues({
                                      ...restInitials,
                                      item_batches: preservedBatches,
                                    });
                                    setOpenBatchIndex(null);
                                  }}
                                  text={"Add New Batch"}
                                />
                              )}
                            </div>
                            <div
                              className="overflow-y-auto"
                              style={{
                                height: `calc(100% - 8rem)`,
                                minHeight: `calc(100% - 8rem)`,
                              }}
                            >
                              {batches?.map((batch, batchIndex) => {
                                const isOpen = openBatchIndex === batchIndex;

                                return (
                                  <div
                                    key={batchIndex}
                                    className="mt-4 border border-[var(--popover-border-color)] rounded-[8px] bg-[var(--slide-panel-accordian-bg)] overflow-hidden"
                                  >
                                    {/* Header */}
                                    <div
                                      className={`h-[44px] cursor-pointer flex justify-between px-3 ${isOpen
                                        ? "border-b border-b-[var(--popover-border-color)]"
                                        : ""
                                        }`}
                                      onClick={() => toggleBatch(batchIndex)}
                                    >
                                      <div className="flex h-full items-center gap-2">
                                        <Package
                                          size={"20px"}
                                          color="var(--slide-panel-label)"
                                        />
                                        <span className="text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)]">
                                          {batch.item_batch_number}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)]">
                                        MRP : ₹{batch.item_batch_unit_price}
                                        <ChevronDownIcon
                                          className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                                            }`}
                                        />
                                      </div>
                                    </div>

                                    {/* Body */}
                                    {isOpen && (
                                      <div className="p-3 bg-[var(--slide-panel-accordian-open-bg)]">
                                        <div className="flex flex-wrap">
                                          {" "}
                                          {[
                                            {
                                              label: "Quantity",
                                              value:
                                                batch?.item_batch_quantity || 0,
                                            },
                                            {
                                              label: "Free Qty",
                                              value:
                                                batch?.item_batch_free_quantity,
                                            },
                                            {
                                              label: "MRP (Per 1 Qty)",
                                              value:
                                                batch?.item_batch_unit_price,
                                            },
                                            {
                                              label: "Base PR (Per 1 Qty)",
                                              value:
                                                batch?.item_batch_purchase_rate,
                                            },
                                            {
                                              label: "From Bin",
                                              value:
                                                batch?.from_bin_location || 0,
                                            },
                                            {
                                              label: "To Bin",
                                              value:
                                                batch?.to_bin_location || 0,
                                            },
                                          ]?.map((bat, index) => (
                                            <div
                                              className={`flex flex-col gap-1 w-[50%]   mt-[1rem]`}
                                            >
                                              <div className="text-[var(--slide-panel-label)] text-[length:var(--slide-panel-label-fs)] font-[var(--slide-label-value-fw)]">
                                                {bat?.label}
                                              </div>
                                              <div className="text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)] text-wrap">
                                                {bat?.value}
                                              </div>
                                            </div>
                                          ))}
                                        </div>

                                        {!disabled && (
                                          <div className="flex justify-between mt-[1rem]">
                                            <AddButton
                                              onclkFn={() => {
                                                handleDeleteBatch(batchIndex);
                                              }}
                                              prefixIcon={false}
                                              text={"Delete"}
                                              className="border rounded-[6px]  border-[var(--status-badge-red-border)] text-[length:--status-badge-special-fs] font-[var(--status-badge-fw)]     text-[var(--status-badge-red-fg)] bg-[var(--status-badge-red-bg)] px-3 h-[36px]"
                                              type="button"
                                            />
                                            <AddButton
                                              onclkFn={() => {
                                                setValues(batch);
                                                setNewBatch(true);
                                              }}
                                              prefixIcon={false}
                                              text={"Edit Batch"}
                                              className="border rounded-[6px]  border-[var(--status-badge-blue-border)] text-[length:--status-badge-special-fs] font-[var(--status-badge-fw)]     text-[var(--status-badge-blue-fg)] bg-[var(--status-badge-blue-bg)] px-3 h-[36px]"
                                              type="button"
                                            />
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="h-[5rem]  absolute bottom-0 bg-white  flex justify-between items-center px-3 w-full">
                          <div></div>
                          <AddButton
                            onclkFn={() => {
                              if (newBatch) {
                                handleSubmit();
                                handleShowErrorToast("", errors, values);
                              } else {
                                setOpen(false);
                              }
                            }}
                            prefixIcon={false}
                            text={
                              newBatch && openBatchIndex
                                ? "Update Batch"
                                : newBatch
                                  ? "Add Batch"
                                  : "Done"
                            }
                            className="
    flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
    bg-[var(--primary-button-bg-color)] cursor-pointer
    text-[var(--primary-button-fg-color)]
    border border-[var(--primary-button-bg-color)]
    hover:bg-[var(--primary-button-hover-color)]
    text-[length:var(--primary-button-font-size)] 
    font-[var(--primary-button-font-weight)]
  "
                            type="button"
                          />
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </Dialog.Panel>
            </Transition.Child>
          </div>
          <LoadingOverlay isLoading={loading} />
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default StockTransferBatchesPanel;
